import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Wallet,
  ShieldCheck,
  Sparkles,
  Inbox,
  Calendar,
  Receipt,
} from "lucide-react";
import Pagination from "../../../components/common/Pagination";
import {
  getAllAdvanceRequests,
  deleteAdvanceRequest,
  approveAdvance,
  rejectAdvance,
} from "../../../api/finance/advance/advanceRequests";
import {
  showErrorToast,
  showSuccessToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import AdvanceFilter from "./AdvanceFilter";
import AdvanceCreateModal from "./AdvanceCreateModal";
import AdvanceDetailModal from "./AdvanceDetailModal";
import AdvanceSummaryCards from "./AdvanceSummaryCards";
import AdvancePermissionModal from "./AdvancePermissionModal";
import PendingAdvanceList from "./PendingAdvanceList";
import { sortPendingFinanceCards } from "../shared/sortPendingFinanceCards";
import AdvanceRowActionMenu from "./AdvanceRowActionMenu";
import AdvancePrintModal from "./AdvancePrintModal";
import { formatDateVN } from "../../../utils/formatUtils";
import { hasPermission } from "../../../utils/permissionUtils";
import { getBranchById } from "../../../api/hrm/branches/info";
import { openAdvanceAttachment } from "../../../utils/openCachedAttachment";
import { currentPeriodDefaults } from "../../reports/finance/financePeriodUtils";

const AdvanceListPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const period = currentPeriodDefaults();
    return {
      keyword: "",
      branch_id: "",
      department_id: "",
      status: "",
      period_type: period.period_type,
      year: period.year,
      quarter: period.quarter,
      month: period.month,
      from_date: period.from_date,
      to_date: period.to_date,
    };
  });
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [editId, setEditId] = useState(null);
  const [copyFromId, setCopyFromId] = useState(null);
  const [printId, setPrintId] = useState(null);
  const [openActionId, setOpenActionId] = useState(null);
  const [showPermission, setShowPermission] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [affiliationType, setAffiliationType] = useState("");

  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);

  const roleNames = useMemo(
    () => (Array.isArray(currentUser?.roles) ? currentUser.roles : []).map((r) => String(r || "").toLowerCase().trim()),
    [currentUser]
  );
  const isSuperAdmin =
    roleNames.includes("super admin") || roleNames.includes("superadmin");
  const isCOO = roleNames.includes("giám đốc điều hành") || roleNames.includes("coo");
  const canViewPermissionControl =
    (["hq", "affiliated"].includes(affiliationType) && isSuperAdmin) ||
    (affiliationType === "franchise" && isCOO);

  const affiliationBadge = useMemo(() => {
    switch (affiliationType) {
      case "hq":
        return {
          label: "HQ",
          className: "bg-violet-50 text-violet-700 border-violet-200",
        };
      case "affiliated":
        return {
          label: "Trực thuộc",
          className: "bg-blue-50 text-blue-700 border-blue-200",
        };
      case "franchise":
        return {
          label: "Nhượng quyền",
          className: "bg-amber-50 text-amber-700 border-amber-200",
        };
      default:
        return {
          label: "Chưa xác định",
          className: "bg-gray-50 text-gray-600 border-gray-200",
        };
    }
  }, [affiliationType]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await getAllAdvanceRequests();
      setData(res || []);
    } catch (err) {
      console.error(err);
      showErrorToast("Không thể tải danh sách đề nghị tạm ứng.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const branchId = currentUser?.branch?.id || currentUser?.branch_id || "";
    if (!branchId) {
      setAffiliationType("");
      return;
    }
    getBranchById(branchId)
      .then((res) => setAffiliationType(String(res?.affiliation_type || "").toLowerCase()))
      .catch(() => setAffiliationType(""));
  }, [currentUser]);

  const scopedData = useMemo(() => {
    return data.filter((item) => {
      if (filters.keyword) {
        const keyword = filters.keyword.toLowerCase();
        const ok =
          item.request_code?.toLowerCase().includes(keyword) ||
          item.employee_name?.toLowerCase().includes(keyword);
        if (!ok) return false;
      }

      if (filters.branch_id && item.branch_id && item.branch_id !== filters.branch_id)
        return false;

      if (
        filters.department_id &&
        item.department_id &&
        item.department_id !== filters.department_id
      )
        return false;

      const rd = item.request_date
        ? String(item.request_date).slice(0, 10)
        : "";
      if (filters.from_date && rd && rd < filters.from_date) return false;
      if (filters.to_date && rd && rd > filters.to_date) return false;

      return true;
    });
  }, [
    data,
    filters.keyword,
    filters.branch_id,
    filters.department_id,
    filters.from_date,
    filters.to_date,
  ]);

  const filteredData = useMemo(() => {
    if (!filters.status) return scopedData;
    return scopedData.filter((item) => item.status === filters.status);
  }, [scopedData, filters.status]);

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredData.slice(start, start + pageSize);
  }, [filteredData, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  const summaryData = useMemo(() => {
    const summary = {
      draft: { count: 0, amount: 0 },
      waiting: { count: 0, amount: 0 },
      approved: { count: 0, amount: 0 },
      rejected: { count: 0, amount: 0 },
      completed: { count: 0, amount: 0 },
    };

    scopedData.forEach((item) => {
      if (!summary[item.status]) return;
      summary[item.status].count += 1;
      summary[item.status].amount += Number(item.advance_amount || 0);
    });

    return summary;
  }, [scopedData]);

  const mobileStatusTone = (status) => {
    switch (status) {
      case "waiting":
        return {
          card: "bg-amber-50 border-amber-300",
          divider: "border-amber-200/80",
          amount: "text-amber-700",
        };
      case "approved":
        return {
          card: "bg-emerald-50 border-emerald-300",
          divider: "border-emerald-200/80",
          amount: "text-emerald-700",
        };
      case "rejected":
        return {
          card: "bg-red-50 border-red-300",
          divider: "border-red-200/80",
          amount: "text-red-700",
        };
      case "completed":
        return {
          card: "bg-blue-50 border-blue-300",
          divider: "border-blue-200/80",
          amount: "text-blue-700",
        };
      case "draft":
      default:
        return {
          card: "bg-slate-50 border-slate-300",
          divider: "border-slate-200/80",
          amount: "text-slate-700",
        };
    }
  };

  const getStatusBadge = (status) => {
    let label = "Không xác định";
    let colorClass = "bg-gray-50 text-gray-500 border-gray-100";

    switch (status) {
      case "draft":
        label = "Nháp";
        colorClass = "bg-slate-100 text-slate-600 border-slate-200";
        break;
      case "waiting":
        label = "Chờ duyệt";
        colorClass = "bg-amber-50 text-amber-700 border-amber-100";
        break;
      case "approved":
        label = "Đã duyệt";
        colorClass = "bg-emerald-50 text-emerald-700 border-emerald-100";
        break;
      case "rejected":
        label = "Từ chối";
        colorClass = "bg-red-50 text-red-700 border-red-100";
        break;
      case "completed":
        label = "Đã quyết toán";
        colorClass = "bg-blue-50 text-blue-700 border-blue-100";
        break;
      default:
        break;
    }

    return (
      <span
        className={`inline-flex shrink-0 max-w-[7.5rem] truncate px-2 py-0.5 md:px-3 md:py-1 rounded-full text-[9px] md:text-[10px] font-black uppercase tracking-wider border whitespace-nowrap ${colorClass}`}
        title={label}
      >
        {label}
      </span>
    );
  };

  const pendingForCards = useMemo(
    () =>
      sortPendingFinanceCards(
        scopedData.filter((x) => x.status === "waiting")
      ),
    [scopedData]
  );

  const getAttachedFileUrl = (item) => item?.pdf_url || item?.attached_file_url || "";

  const openCreateDrawer = ({ edit = null, copyFrom = null } = {}) => {
    setEditId(edit);
    setCopyFromId(copyFrom);
    setShowDrawer(true);
  };

  const handleViewItem = (item) => setSelectedItem(item);

  const handleDownloadItem = (item) => setPrintId(item?.id || null);

  const handleCopyItem = (item) => {
    if (!hasPermission("finance.advance.create", "C")) {
      showErrorToast("Bạn không có quyền tạo phiếu tạm ứng.");
      return;
    }
    openCreateDrawer({ copyFrom: item.id });
  };

  const handleEditItem = (item) => {
    if (item?.status !== "draft") {
      showErrorToast("Chỉ sửa được phiếu ở trạng thái Nháp.");
      return;
    }
    if (item?.employee_id !== currentUser?.id) {
      showErrorToast("Chỉ sửa được phiếu của chính mình.");
      return;
    }
    if (!hasPermission("finance.advance.create", "C")) {
      showErrorToast("Bạn không có quyền sửa phiếu tạm ứng.");
      return;
    }
    openCreateDrawer({ edit: item.id });
  };

  const handleDelete = async (id) => {
    const confirm = await showConfirmDialog({
      title: "Hủy đề nghị tạm ứng",
      text: "Bạn có chắc muốn hủy đề nghị này? Thao tác không hoàn tác.",
      confirmText: "Hủy đề nghị",
      cancelText: "Giữ lại",
    });
    if (!confirm?.isConfirmed) return;

    try {
      await deleteAdvanceRequest(id);
      showSuccessToast("Đã xóa đề nghị.");
      setSelectedItem(null);
      fetchData();
    } catch (err) {
      showErrorToast(
        err?.response?.data?.message || "Không thể xóa đề nghị."
      );
    }
  };

  const handleUpdateStatus = async (id, status, rejectReason) => {
    try {
      if (status === "submitted") {
        fetchData();
        setSelectedItem(null);
        return;
      }
      if (status === "approved") await approveAdvance(id);
      if (status === "rejected") await rejectAdvance(id, rejectReason);
      showSuccessToast("Cập nhật trạng thái thành công");
      fetchData();
      setSelectedItem(null);
    } catch (err) {
      showErrorToast(
        err.response?.data?.message || "Không thể cập nhật trạng thái"
      );
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans">
      <div className="p-4 md:p-6">
        <header className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-red-700 p-2.5 rounded-xl text-white shadow-lg shadow-red-200">
              <Wallet size={24} />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg sm:text-xl font-black text-gray-800 tracking-tight uppercase">
                  <span className="md:hidden">Đề nghị <span className="text-red-700">tạm ứng</span></span>
                  <span className="hidden md:inline">
                    Quản lý <span className="text-red-700">Đề nghị tạm ứng</span>
                  </span>
                </h1>
                <span
                  className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider border ${affiliationBadge.className}`}
                >
                  {affiliationBadge.label}
                </span>
              </div>
              <p className="text-[11px] text-gray-500 font-bold uppercase tracking-widest mt-0.5">
                Phê duyệt nhiều cấp
              </p>
            </div>
          </div>

          <div className="hidden md:flex flex-wrap items-center gap-2">
            {canViewPermissionControl && (
              <button
                type="button"
                onClick={() => setShowPermission(true)}
                className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-2xl text-xs font-bold hover:border-red-300 hover:text-red-700 transition-all flex items-center gap-2 shadow-sm"
              >
                <ShieldCheck size={16} />
                Phân quyền
              </button>
            )}
            {hasPermission("finance.advance.access", "R") && (
              <button
                type="button"
                onClick={() =>
                  showSuccessToast("Product tour cho module tạm ứng sẽ được bật ở bản tiếp theo.")
                }
                className="px-4 py-2.5 bg-white border border-gray-200 text-gray-700 rounded-2xl text-xs font-bold hover:border-red-300 hover:text-red-700 transition-all flex items-center gap-2 shadow-sm"
              >
                <Sparkles size={16} />
                Product tour
              </button>
            )}
          </div>
        </header>

        <main>
        <AdvanceSummaryCards
          summaryData={summaryData}
          selectedStatus={filters.status}
          onSelectStatus={(status) =>
            setFilters((prev) => ({ ...prev, status }))
          }
        />

        <AdvanceFilter
          onFilterChange={(f) => setFilters(f)}
          onNewClick={() => openCreateDrawer()}
          onRefresh={fetchData}
          status={filters.status}
        />

        <PendingAdvanceList
          data={pendingForCards}
          onSelect={(item) => setSelectedItem(item)}
        />

        <h2 className="font-semibold text-sm md:text-base text-gray-700 mb-3 ml-1 md:ml-2">
          Bảng danh sách phiếu tạm ứng ({filteredData.length} phiếu)
        </h2>

        {/* Mobile: card list — chỉ xem */}
        <div className="md:hidden space-y-3 relative min-h-[120px]">
          {loading && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10 rounded-2xl">
              <div className="animate-spin w-9 h-9 border-2 border-red-600 border-t-transparent rounded-full" />
            </div>
          )}
          {!loading && paginatedData.length === 0 && (
            <div className="bg-white rounded-2xl border-2 border-gray-300 px-4 py-12 text-center">
              <Inbox className="w-10 h-10 mx-auto text-gray-200 mb-2" strokeWidth={1} />
              <p className="text-gray-400 text-sm italic">Không có dữ liệu phù hợp.</p>
            </div>
          )}
          {paginatedData.map((item) => {
            const tone = mobileStatusTone(item.status);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setSelectedItem(item)}
                className={`w-full text-left rounded-2xl border-2 shadow-sm p-4 active:scale-[0.99] transition-transform ${tone.card}`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <p className="text-sm font-black text-gray-800 uppercase tracking-tight truncate">
                      {item.request_code}
                    </p>
                    <p className="text-xs text-gray-700 mt-0.5 truncate font-medium">
                      {item.employee_name || "—"}
                    </p>
                    <p className="text-[10px] text-gray-500 font-bold mt-1 flex items-center gap-1">
                      <Calendar size={10} className="shrink-0" />
                      {formatDateVN(item.request_date)}
                      {item.branch_name ? (
                        <span className="truncate"> · {item.branch_name}</span>
                      ) : null}
                    </p>
                  </div>
                  {getStatusBadge(item.status)}
                </div>
                <div className={`flex items-end justify-between gap-3 pt-3 border-t ${tone.divider}`}>
                  <p className="text-xs text-gray-600 min-w-0 line-clamp-2 whitespace-pre-wrap break-words">
                    {item.advance_reason || "—"}
                  </p>
                  <p className={`text-base font-black tabular-nums shrink-0 ${tone.amount}`}>
                    {Number(item.advance_amount || 0).toLocaleString("vi-VN")} ₫
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Desktop: table */}
        <div className="hidden md:block bg-white rounded-[2rem] border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto relative min-h-[200px]">
            {loading && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center z-10 rounded-[2rem]">
                <div className="animate-spin w-10 h-10 border-2 border-red-600 border-t-transparent rounded-full" />
              </div>
            )}
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-gray-50/80 border-b border-gray-100">
                  <th className="px-4 py-4 text-[11px] font-black uppercase text-gray-400 text-center w-14">
                    STT
                  </th>
                  <th className="px-6 py-4 text-[11px] font-black uppercase text-gray-400">
                    Mã phiếu
                  </th>
                  <th className="px-6 py-4 text-[11px] font-black uppercase text-gray-400">
                    Nhân viên / Đơn vị
                  </th>
                  <th className="px-6 py-4 text-[11px] font-black uppercase text-gray-400 whitespace-nowrap">
                    Số tiền
                  </th>
                  <th className="px-6 py-4 text-[11px] font-black uppercase text-gray-400">
                    Lý do
                  </th>
                  <th className="px-6 py-4 text-[11px] font-black uppercase text-gray-400 text-left min-w-[8.5rem] whitespace-nowrap">
                    Trạng thái
                  </th>
                  <th className="px-4 py-4 text-[11px] font-black uppercase text-gray-400 text-center w-16">
                    File
                  </th>
                  <th className="px-4 py-4 text-[11px] font-black uppercase text-gray-400 text-center w-[72px]">
                    Thao tác
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {!loading && paginatedData.length === 0 && (
                  <tr>
                    <td colSpan={8} className="px-6 py-16 text-center">
                      <Inbox
                        className="w-12 h-12 mx-auto text-gray-200 mb-3"
                        strokeWidth={1}
                      />
                      <p className="text-gray-400 text-sm font-medium italic">
                        Không có dữ liệu phù hợp bộ lọc.
                      </p>
                    </td>
                  </tr>
                )}
                {paginatedData.map((item, index) => (
                  <tr
                    key={item.id}
                    onClick={() => setSelectedItem(item)}
                    className="group hover:bg-red-50/40 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-4 text-center text-xs font-bold text-gray-400 align-top">
                      {(currentPage - 1) * pageSize + index + 1}
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex items-start gap-2">
                        <Receipt
                          size={16}
                          className="text-gray-300 group-hover:text-red-400 mt-0.5 shrink-0"
                        />
                        <div className="min-w-0">
                          <span className="text-sm font-bold text-gray-800 group-hover:text-red-700 uppercase tracking-tight">
                            {item.request_code}
                          </span>
                          <div className="flex items-center gap-1 text-[11px] text-gray-400 font-semibold mt-1">
                            <Calendar size={11} className="shrink-0" />
                            {formatDateVN(item.request_date)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-xl bg-red-50 text-red-700 flex items-center justify-center text-xs font-black group-hover:bg-red-100 shrink-0">
                          {item.employee_name?.split(" ").pop()?.charAt(0) ||
                            "?"}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-800">
                            {item.employee_name || "—"}
                          </div>
                          <div className="text-[10px] text-gray-500 uppercase font-medium">
                            {item.branch_name || "—"} •{" "}
                            {item.department_name || "—"}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top whitespace-nowrap">
                      <div className="text-sm font-black text-red-700 tabular-nums">
                        {Number(item.advance_amount || 0).toLocaleString("vi-VN")} ₫
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top">
                      <div className="max-w-[280px] text-sm text-gray-600 whitespace-pre-wrap break-words leading-relaxed">
                        {item.advance_reason || "—"}
                      </div>
                    </td>
                    <td className="px-6 py-4 align-top text-left min-w-[8.5rem]">
                      {getStatusBadge(item.status)}
                    </td>
                    <td
                      className="px-4 py-4 align-top text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {getAttachedFileUrl(item) ? (
                        <button
                          type="button"
                          onClick={() => openAdvanceAttachment(item.id)}
                          title="Xem file đính kèm"
                          className="inline-flex items-center justify-center h-8 min-w-[2.25rem] px-1.5 rounded-lg bg-red-50 text-red-700 border border-red-100 text-[9px] font-black tracking-wider hover:bg-red-100"
                        >
                          PDF
                        </button>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td
                      className="px-4 py-4 align-top text-center"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <AdvanceRowActionMenu
                        item={item}
                        isOpen={openActionId === `desktop-${item.id}`}
                        onOpenChange={(open) =>
                          setOpenActionId(open ? `desktop-${item.id}` : null)
                        }
                        onView={handleViewItem}
                        onDownload={handleDownloadItem}
                        onCopy={handleCopyItem}
                        onEdit={handleEditItem}
                        onDelete={(row) => handleDelete(row.id)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredData.length > 0 && (
          <Pagination
            totalRecords={filteredData.length}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
          />
        )}
        </main>
      </div>

      <AdvanceDetailModal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        advanceId={selectedItem?.id}
        onUpdateStatus={handleUpdateStatus}
        onDelete={handleDelete}
        onEdit={(id) => {
          setSelectedItem(null);
          openCreateDrawer({ edit: id });
        }}
        onReminded={fetchData}
      />

      <AdvanceCreateModal
        key={
          editId
            ? `edit-${editId}`
            : copyFromId
              ? `copy-${copyFromId}`
              : "create"
        }
        isOpen={showDrawer}
        editId={editId}
        copyFromId={copyFromId}
        onClose={() => {
          setShowDrawer(false);
          setEditId(null);
          setCopyFromId(null);
        }}
        onSaved={() => {
          fetchData();
          setShowDrawer(false);
          setEditId(null);
          setCopyFromId(null);
        }}
      />

      <AdvancePrintModal
        isOpen={!!printId}
        advanceId={printId}
        onClose={() => setPrintId(null)}
      />

      <AdvancePermissionModal
        isOpen={showPermission && canViewPermissionControl}
        onClose={() => setShowPermission(false)}
      />
    </div>
  );
};

export default AdvanceListPage;
